import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  Input,
  Textarea,
  Divider,
  Spinner,
  Tabs,
  Tab,
  Select,
  SelectItem,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Chip,
} from "@heroui/react";
import {
  ArrowLeft,
  Save,
  Edit,
  Plus,
  TrendingUp,
  TrendingDown,
  Package,
  AlertTriangle,
} from "lucide-react";

import { inventoryService } from "@/services/inventoryService";
import { formatDate, formatCurrency } from "@/utils/formatters";

export default function InventoryItemPage() {
  const { id } = useParams<{ id: string }>();
  const itemId = id ? parseInt(id) : 0;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [ingredient, setIngredient] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Form data for editing
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    description: "",
    unit: "",
    minimum_stock: 0,
    cost_per_unit: 0,
    supplier: "",
    is_active: true,
  });

  // Stock adjustment modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [adjustmentData, setAdjustmentData] = useState({
    quantity: 0,
    reason: "",
    notes: "",
  });
  const [adjusting, setAdjusting] = useState(false);

  useEffect(() => {
    if (itemId) {
      fetchInventoryItem();
    }
  }, [itemId]);

  const fetchInventoryItem = async () => {
    try {
      setLoading(true);
      setError("");

      const data = await inventoryService.getInventoryItemById(itemId);

      setIngredient(data);

      // Initialize form data
      setFormData({
        name: data.name || "",
        code: data.code || "",
        description: data.description || "",
        unit: data.unit || "",
        minimum_stock: parseFloat(data.minimum_stock) || 0,
        cost_per_unit: data.cost_per_unit / 100 || 0, // Convert cents to dollars for display
        supplier: data.supplier || "",
        is_active: data.is_active,
      });

      // Fetch transactions
      fetchTransactions();
    } catch (err) {
      console.error("Error fetching inventory item:", err);
      setError("Failed to load inventory item");
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      setTransactionsLoading(true);

      const params = {
        ingredient_id: itemId,
        limit: 10,
      };

      const data = await inventoryService.getInventoryTransactions(params);

      setTransactions(data);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    } finally {
      setTransactionsLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: (e.target as HTMLInputElement).checked,
      });
    } else if (name === "cost_per_unit" || name === "minimum_stock") {
      setFormData({
        ...formData,
        [name]: parseFloat(value) || 0,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");

      // Validate form
      if (!formData.name.trim()) {
        setError("Name is required");
        setSaving(false);

        return;
      }

      if (!formData.unit.trim()) {
        setError("Unit is required");
        setSaving(false);

        return;
      }

      // Convert dollars to cents for API
      const costInCents = Math.round(formData.cost_per_unit * 100);

      await inventoryService.updateInventoryItem(itemId, {
        name: formData.name,
        code: formData.code,
        description: formData.description,
        unit: formData.unit,
        minimum_stock: formData.minimum_stock,
        cost_per_unit: costInCents,
        supplier: formData.supplier,
        is_active: formData.is_active,
      });

      // Refresh data
      await fetchInventoryItem();
      setEditMode(false);
    } catch (err) {
      console.error("Error updating inventory item:", err);
      setError("Failed to update inventory item");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/inventory");
  };

  const handleAdjustmentChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "quantity") {
      setAdjustmentData({
        ...adjustmentData,
        [name]: parseFloat(value) || 0,
      });
    } else {
      setAdjustmentData({
        ...adjustmentData,
        [name]: value,
      });
    }
  };

  const handleStockAdjustment = async () => {
    try {
      setAdjusting(true);

      // Validate form
      if (adjustmentData.quantity === 0) {
        setError("Quantity is required and must not be zero");
        setAdjusting(false);

        return;
      }

      if (!adjustmentData.reason.trim()) {
        setError("Reason is required");
        setAdjusting(false);

        return;
      }

      // Call API to adjust stock
      await inventoryService.updateItemStock(
        itemId,
        parseFloat(ingredient.current_stock) + adjustmentData.quantity,
        adjustmentData.reason,
      );

      // Refresh data
      await fetchInventoryItem();
      await fetchTransactions();

      // Close modal and reset form
      onClose();
      setAdjustmentData({
        quantity: 0,
        reason: "",
        notes: "",
      });
    } catch (err) {
      console.error("Error adjusting stock:", err);
      setError("Failed to adjust stock");
    } finally {
      setAdjusting(false);
    }
  };

  const renderStockStatus = () => {
    if (!ingredient) return null;

    const currentStock = parseFloat(ingredient.current_stock);
    const minStock = parseFloat(ingredient.minimum_stock);

    if (currentStock <= 0) {
      return (
        <Chip color="danger" variant="flat">
          Out of Stock
        </Chip>
      );
    } else if (currentStock < minStock) {
      return (
        <Chip color="warning" variant="flat">
          Low Stock
        </Chip>
      );
    } else {
      return (
        <Chip color="success" variant="flat">
          In Stock
        </Chip>
      );
    }
  };

  const renderTransactionType = (type: string) => {
    switch (type) {
      case "purchase":
        return (
          <Chip color="success" variant="flat">
            Purchase
          </Chip>
        );
      case "usage":
        return (
          <Chip color="primary" variant="flat">
            Usage
          </Chip>
        );
      case "adjustment":
        return (
          <Chip color="secondary" variant="flat">
            Adjustment
          </Chip>
        );
      case "waste":
        return (
          <Chip color="danger" variant="flat">
            Waste
          </Chip>
        );
      default:
        return <Chip variant="flat">{type}</Chip>;
    }
  };

  if (loading && !ingredient) {
    return (
      <div className="flex justify-center items-center p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error && !ingredient) {
    return (
      <div className="p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Button variant="light" onPress={handleBack}>
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-2xl font-bold">Error</h1>
        </div>
        <Card className="p-6 text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-danger mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            Failed to Load Inventory Item
          </h2>
          <p className="text-default-500 mb-4">{error}</p>
          <Button color="primary" onPress={handleBack}>
            Back to Inventory
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-2">
          <Button isIconOnly variant="light" onPress={handleBack}>
            <ArrowLeft size={18} />
          </Button>
          <h1 className="text-2xl font-bold">
            {ingredient?.name}
            {!ingredient?.is_active && (
              <Chip className="ml-2" color="danger" variant="flat">
                Inactive
              </Chip>
            )}
          </h1>
        </div>
        <div className="flex gap-2">
          {editMode ? (
            <>
              <Button
                color="primary"
                variant="flat"
                onPress={() => setEditMode(false)}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                isLoading={saving}
                startContent={<Save size={16} />}
                onPress={handleSave}
              >
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button
                color="primary"
                startContent={<Plus size={16} />}
                variant="flat"
                onPress={onOpen}
              >
                Adjust Stock
              </Button>
              <Button
                color="primary"
                startContent={<Edit size={16} />}
                onPress={() => setEditMode(true)}
              >
                Edit
              </Button>
            </>
          )}
        </div>
      </div>

      <Tabs>
        <Tab key="details" title="Details">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4">
            {/* Left column - Details */}
            <Card className="p-6 space-y-6 col-span-1">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Details</h2>
                {renderStockStatus()}
              </div>

              <Divider />

              <div className="space-y-4">
                {editMode ? (
                  <>
                    <Input
                      isRequired
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="Code"
                      name="code"
                      value={formData.code}
                      onChange={handleInputChange}
                    />
                    <Textarea
                      label="Description"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        isRequired
                        label="Unit"
                        name="unit"
                        value={formData.unit}
                        onChange={handleInputChange}
                      />
                      <Input
                        label="Cost per Unit"
                        min="0"
                        name="cost_per_unit"
                        startContent={
                          <div className="pointer-events-none flex items-center">
                            <span className="text-default-400 text-small">
                              $
                            </span>
                          </div>
                        }
                        step="0.01"
                        type="number"
                        value={formData.cost_per_unit.toString()}
                        onChange={handleInputChange}
                      />
                    </div>
                    <Input
                      label="Minimum Stock"
                      min="0"
                      name="minimum_stock"
                      step="0.01"
                      type="number"
                      value={formData.minimum_stock.toString()}
                      onChange={handleInputChange}
                    />
                    <Input
                      label="Supplier"
                      name="supplier"
                      value={formData.supplier}
                      onChange={handleInputChange}
                    />
                    <div className="flex items-center gap-2">
                      <input
                        checked={formData.is_active}
                        className="h-4 w-4 rounded border-gray-300 focus:ring-primary"
                        id="is_active"
                        name="is_active"
                        type="checkbox"
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            is_active: e.target.checked,
                          })
                        }
                      />
                      <label
                        className="text-sm font-medium"
                        htmlFor="is_active"
                      >
                        Active
                      </label>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-default-600">
                        Name
                      </label>
                      <p>{ingredient?.name}</p>
                    </div>

                    {ingredient?.code && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-default-600">
                          Code
                        </label>
                        <p>{ingredient?.code}</p>
                      </div>
                    )}

                    {ingredient?.description && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-default-600">
                          Description
                        </label>
                        <p className="text-sm">{ingredient?.description}</p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-default-600">
                          Unit
                        </label>
                        <p>{ingredient?.unit}</p>
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-default-600">
                          Cost per Unit
                        </label>
                        <p>{formatCurrency(ingredient?.cost_per_unit)}</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-default-600">
                        Minimum Stock
                      </label>
                      <p>
                        {ingredient?.minimum_stock} {ingredient?.unit}
                      </p>
                    </div>

                    {ingredient?.supplier && (
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-default-600">
                          Supplier
                        </label>
                        <p>{ingredient?.supplier}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Card>

            {/* Right column - Stock & Transactions */}
            <Card className="p-6 space-y-6 col-span-1 lg:col-span-2">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Stock Information</h2>
                <Button
                  color="primary"
                  size="sm"
                  startContent={<Plus size={16} />}
                  onPress={onOpen}
                >
                  Adjust Stock
                </Button>
              </div>

              <Divider />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-default-100 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <Package className="h-6 w-6 text-primary" />
                    <span className="text-xs text-default-400">Current</span>
                  </div>
                  <p className="mt-2 text-2xl font-semibold">
                    {ingredient?.current_stock} {ingredient?.unit}
                  </p>
                  <p className="text-sm text-default-500">Current Stock</p>
                </div>

                <div className="bg-default-100 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <AlertTriangle className="h-6 w-6 text-warning" />
                    <span className="text-xs text-default-400">Minimum</span>
                  </div>
                  <p className="mt-2 text-2xl font-semibold">
                    {ingredient?.minimum_stock} {ingredient?.unit}
                  </p>
                  <p className="text-sm text-default-500">Minimum Stock</p>
                </div>

                <div className="bg-default-100 p-4 rounded-lg">
                  <div className="flex items-center justify-between">
                    <TrendingUp className="h-6 w-6 text-success" />
                    <span className="text-xs text-default-400">Value</span>
                  </div>
                  <p className="mt-2 text-2xl font-semibold">
                    {formatCurrency(
                      parseFloat(ingredient?.current_stock) *
                        ingredient?.cost_per_unit,
                    )}
                  </p>
                  <p className="text-sm text-default-500">Current Value</p>
                </div>
              </div>

              <Divider />

              <div className="space-y-4">
                <h3 className="text-lg font-medium">Recent Transactions</h3>

                {transactionsLoading ? (
                  <div className="flex justify-center p-4">
                    <Spinner size="md" />
                  </div>
                ) : transactions.length > 0 ? (
                  <Table aria-label="Recent transactions">
                    <TableHeader>
                      <TableColumn>TYPE</TableColumn>
                      <TableColumn>DATE</TableColumn>
                      <TableColumn>QUANTITY</TableColumn>
                      <TableColumn>REASON</TableColumn>
                      <TableColumn>USER</TableColumn>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            {renderTransactionType(
                              transaction.transaction_type,
                            )}
                          </TableCell>
                          <TableCell>
                            {formatDate(transaction.created_at, "MMM D, YYYY")}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              {parseFloat(transaction.quantity) > 0 ? (
                                <TrendingUp className="h-4 w-4 text-success mr-1" />
                              ) : (
                                <TrendingDown className="h-4 w-4 text-danger mr-1" />
                              )}
                              <span>
                                {parseFloat(transaction.quantity) > 0
                                  ? "+"
                                  : ""}
                                {transaction.quantity} {ingredient?.unit}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>{transaction.notes || "N/A"}</TableCell>
                          <TableCell>{transaction.created_by_name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center p-6 border border-dashed rounded-md">
                    <p className="text-default-500">No transactions found</p>
                  </div>
                )}

                <div className="flex justify-center">
                  <Button
                    color="primary"
                    variant="flat"
                    onPress={() =>
                      navigate(
                        `/inventory/transactions?ingredient_id=${itemId}`,
                      )
                    }
                  >
                    View All Transactions
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </Tab>

        <Tab key="transactions" title="Transactions">
          <Card className="p-6 mt-4">
            <p className="text-center">
              Full transaction history would be displayed here.
            </p>
          </Card>
        </Tab>

        <Tab key="usage" title="Usage Analysis">
          <Card className="p-6 mt-4">
            <p className="text-center">
              Usage analysis charts and data would be displayed here.
            </p>
          </Card>
        </Tab>
      </Tabs>

      {/* Stock Adjustment Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Adjust Stock</ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Current Stock:</span>
                    <span>
                      {ingredient?.current_stock} {ingredient?.unit}
                    </span>
                  </div>

                  <Input
                    isRequired
                    description="Use positive values for additions, negative for removals"
                    label="Quantity to Adjust"
                    name="quantity"
                    type="number"
                    value={adjustmentData.quantity.toString()}
                    onChange={handleAdjustmentChange}
                  />

                  <div className="flex justify-between items-center">
                    <span className="font-medium">New Stock Level:</span>
                    <span>
                      {(
                        parseFloat(ingredient?.current_stock) +
                        adjustmentData.quantity
                      ).toFixed(2)}{" "}
                      {ingredient?.unit}
                    </span>
                  </div>

                  <Select
                    isRequired
                    label="Reason"
                    name="reason"
                    value={adjustmentData.reason}
                    onChange={handleAdjustmentChange}
                  >
                    <SelectItem key="purchase" textValue="Purchase">
                      Purchase
                    </SelectItem>
                    <SelectItem key="count" textValue="Inventory Count">
                      Inventory Count
                    </SelectItem>
                    <SelectItem key="waste" textValue="Waste">
                      Waste
                    </SelectItem>
                    <SelectItem key="damaged" textValue="Damaged">
                      Damaged
                    </SelectItem>
                    <SelectItem key="expired" textValue="Expired">
                      Expired
                    </SelectItem>
                    <SelectItem key="other" textValue="Other">
                      Other
                    </SelectItem>
                  </Select>

                  <Textarea
                    label="Notes"
                    name="notes"
                    placeholder="Add any additional details about this adjustment"
                    value={adjustmentData.notes}
                    onChange={handleAdjustmentChange}
                  />

                  {error && <div className="text-danger text-sm">{error}</div>}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onPress={onClose}>
                  Cancel
                </Button>
                <Button
                  color="primary"
                  isLoading={adjusting}
                  onPress={handleStockAdjustment}
                >
                  Submit Adjustment
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
